/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Button } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { showSuccess } from "~/components/Toast";
import { useCustomer } from "~/context/User/Customer";
import { RouteParams } from "~/models";
import { JiraProject } from "~/models/jiraProject";
import { generateActionSuccessText, isValidDate, useAxios } from "~/tools";
import { ToolsAndIntegrations } from "../BasicCustomerInfo/types";

type AddJiraProjectProps = {
  refetch: () => void;
  fetchCustomer: () => void;
};

function AddJiraProject({ refetch, fetchCustomer }: AddJiraProjectProps) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  const methods = useForm();
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const watcher = methods.watch();

  const state = useParams<RouteParams>();
  const { customerId } = state;

  const { customer } = useCustomer();

  const onSubmit = async (values: JiraProject) => {
    try {
      const updateData = {
        ...customer,
      };

      const tools =
        updateData.toolsAndIntegrations ?? ({} as ToolsAndIntegrations);
      updateData.toolsAndIntegrations = tools;

      const arr = updateData.toolsAndIntegrations?.jiraProjects ?? [];

      tools.jiraProjects = arr.map((p) =>
        p.code === values.code ? { ...values } : { ...p }
      );

      if (!arr.some((p) => p.code === values.code)) {
        tools.jiraProjects = [...tools.jiraProjects, { ...values }];
      }

      await Axios().put(`/customers/${customerId}`, updateData);

      fetchCustomer();
      refetch();

      showSuccess(generateActionSuccessText(`JiraProject`)()("added")());
      setTimeout(() => {
        setIsSidebarOpen(false);
      }, 200);
    } catch (e) {
      showError(e.response);
    }
  };

  return (
    <>
      <Button icon="plus" onClick={() => setIsSidebarOpen(true)}>
        Add JiraProject
      </Button>

      <SidebarForm
        title="Add JiraProject"
        width="36rem"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        <SidebarTextField name="code" label="Jira Code" required />

        <SidebarTextField
          name="reportingStartDate"
          label="Reporting Start Date"
          registerOptions={{ validate: isValidDate }}
        />

        <SidebarTextField
          name="reportingEndDate"
          label="Reporting End Date"
          registerOptions={{ validate: isValidDate }}
        />

        <SidebarFormButtons
          submitEnabled={!!watcher.code}
          onCancel={() => setIsSidebarOpen(false)}
        />
      </SidebarForm>
    </>
  );
}

export { AddJiraProject };
